import type { NextPage } from 'next'
import { gsap } from 'gsap/dist/gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'

import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useState, useRef } from 'react'
import Contact from '../../components/contact/Contact'
import Item, { ItemProps } from '../../components/item/Item'
import Grid from '../../components/grid/Grid'
import ImageQuote from '../../components/imagequote/ImageQuote'
import Careers from '../../components/careers/Careers'
import FadeIn from '../../components/fadeIn/FadeIn'
import CustomImage from '../../components/customImage/CustomImage'

import { useIsMobile } from '../../hooks/useIsDesktop'
import {
  useWindowSize,
  useIntersectionObserver,
  useIsomorphicLayoutEffect,
} from 'usehooks-ts'
import styles from '../../styles/pages/PlatformManufacturing.module.scss'

import CustomMetaData from '../../components/general/customMetaData/CustomMetaData'
import { GenericPageLayoutProps } from '../../components/general/genericPageLayout/GenericPageLayout'

import { useGlobalContentContext } from '../../context/GlobalContentContext'

import { parseGenericPage } from '../../utils/parsers'
import { getAPI, createMarkup } from '../../utils/helpers'
import variables from '../../styles/_constants.module.scss'

import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper'
// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'

if (typeof window !== 'undefined') {
  // Due to SSR we can only register when we are in the browser
  gsap.registerPlugin(ScrollTrigger)
}

type Props = {} & GenericPageLayoutProps

type MapProps = {
  mapsection: any
}

type ProcessStationProps = {
  index: number
  block: any
  handleActive: any
  processImageActive: any
}

const MapSection = (props: MapProps) => {
  const isMobile = useIsMobile()

  const [scrollX, setScrollX] = useState(0)
  const [touchStartX, setTouchStartX] = useState(0)

  const scrollRef = useRef<HTMLDivElement>(null)
  const sliderRef = useRef<HTMLDivElement>(null)
  const sliderInnerRef = useRef<HTMLDivElement>(null)

  const hotspotRefs = useRef<(HTMLDivElement | null)[]>([])

  const [activeHotspot, setActiveHotSpot] = useState<number | null>(null)
  const onHotspotMouseEnter = (index: number) => {
    setActiveHotSpot(index)
  }

  const onHotspotMouseLeave = (index: number) => {
    setActiveHotSpot(-1)
  }

  useIsomorphicLayoutEffect(() => {
    if (hotspotRefs.current) {
      for (let key in hotspotRefs.current) {
        gsap.set(hotspotRefs.current[parseInt(key)], {
          opacity: 0,
          xPercent: -25,
        })
      }
    }
  }, [hotspotRefs])

  useIsomorphicLayoutEffect(() => {
    if (activeHotspot && hotspotRefs.current[activeHotspot]) {
      gsap.set(hotspotRefs.current[activeHotspot], { zIndex: 1 })
      gsap.fromTo(
        hotspotRefs.current[activeHotspot],
        {
          opacity: 0,
          xPercent: -15,
        },
        {
          opacity: 1,
          xPercent: 0,
          duration: 0.5,
          delay: 0.25,
          overwrite: true,
        }
      )
    } else {
      for (let key in hotspotRefs.current) {
        gsap.to(hotspotRefs.current[parseInt(key)], {
          opacity: 0,

          xPercent: 15,
          duration: 0.5,
          overwrite: true,
          onComplete: () => {
            gsap.set(hotspotRefs.current[parseInt(key)], { zIndex: -1 })
          },
        })
      }
    }
  }, [activeHotspot])

  return (
    <div className={styles.mapSectionImageInner}>
      <CustomImage src={props.mapsection} layout="responsive" />

      {props.mapsection.map_section_hotspots.map(
        (hotspot: any, index: number) => (
          <div
            key={index}
            className={styles.mapHotspot}
            style={{
              left: `${hotspot.x_percent}%`,
              top: `${hotspot.y_percent}%`,
            }}
          >
            <div
              onMouseEnter={() => onHotspotMouseEnter(index + 1)}
              onMouseLeave={() => onHotspotMouseLeave(index + 1)}
              onTouchStart={() => onHotspotMouseEnter(index + 1)}
              onTouchEnd={() => onHotspotMouseLeave(index + 1)}
            >
              <div
                className={`${styles.hotSpotId} ${
                  activeHotspot == index + 1 ? styles.hotSpotActive : ''
                }`}
              >
                {hotspot.code.toUpperCase()}
              </div>
              <div className={styles.hotSpotShortTitle}>{hotspot.title}</div>
            </div>
            <div
              className={`${styles.hotSpotDetailContainer} ${
                hotspot.x_percent > 50 ? styles.hotSpotDetailContainerLeft : ''
              }`}
              ref={(ref) => (hotspotRefs.current[index + 1] = ref)}
            >
              <h3 className={styles.hotSpotTitle}>{hotspot.title}</h3>
              <div className={styles.hotSpotSubTitle}>{hotspot.subtitle}</div>
              <div
                className={styles.hotSpotDetail}
                dangerouslySetInnerHTML={createMarkup(hotspot.detail)}
              ></div>
            </div>
          </div>
        )
      )}
    </div>
  )
}

const ProcessSectionBlock = (props: ProcessStationProps) => {
  const isMobile = useIsMobile()
  const ref = useRef<HTMLDivElement>(null)

  useIsomorphicLayoutEffect(() => {
    if (ref.current) {
      ScrollTrigger.matchMedia({
        '(max-width: 768px)': function () {
          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: ref.current,
              start: 'top 75%',
              end: 'top 25%',
              scrub: true,
              onEnter: () => {
                props.handleActive()
              },
              onEnterBack: () => {
                props.handleActive()
              },
            },
          })

          tl.to(ref.current, {
            opacity: 1,
            duration: 1.4,
          })

          tl.to(ref.current, {
            opacity: 0,
            duration: 0.7,
          })

          return function () {
            gsap.set(ref.current, { opacity: 1 })
            tl && tl.kill()
          }
        },
      })
    }
  }, [props, isMobile])

  return (
    <div
      className={styles.processStationsBlock}
      onMouseEnter={props.handleActive}
      ref={ref}
    >
      <div className={styles.processStationsBlockIndex}>
        <span
          className={`${
            props.index == props.processImageActive
              ? styles.processStationsBlockIndexInnerActive
              : styles.processStationsBlockIndexInner
          }`}
        >
          {String(props.index + 1).padStart(2, '0')}
        </span>
      </div>
      <h3 className={styles.processStationsBlockTitle}>{props.block.title}</h3>
      <div
        className={styles.processStationsBlockDetail}
        dangerouslySetInnerHTML={createMarkup(props.block.detail)}
      ></div>
    </div>
  )
}

const PlatformManufacturing = (props: Props) => {
  const { setData } = useGlobalContentContext()

  const [imagesLoaded, setImagesLoaded] = useState(
    props.originalResponse.process_stations_blocks.map(() => 0)
  )
  const [imageOpacity, setImageOpacity] = useState(0.01)

  useEffect(() => {
    setData(props.globalData)
  }, [props.globalData, setData])

  const isMobile = useIsMobile()
  const { width, height } = useWindowSize()

  const [processImageActive, setProcessImageActive] = useState<number>(0)

  useEffect(() => {
    if (processImageActive > 0) {
      setImageOpacity(0)
    }
  }, [processImageActive])

  return (
    <div className={styles.container}>
      <CustomMetaData
        title={props.originalResponse.meta_title}
        description={props.originalResponse.meta_description}
        image={props.originalResponse.meta_image}
      />

      <div className={styles.main}>
        <section className={styles.hero}>
          <div className={styles.heroContainer}>
            <h1 className={styles.heroTitle}>
              <Image
                src="/kavian.svg"
                layout="responsive"
                width={670}
                height={179}
                alt={props.originalResponse.hero.title}
              />
            </h1>
            <h2 className={styles.heroSubTitle}>
              {props.originalResponse.hero.sub_title}
            </h2>
          </div>
          <div className={styles.heroImage}>
            <CustomImage
              src={props.originalResponse.hero}
              layout="fill"
              objectFit="cover"
            />
          </div>
        </section>

        <section className={styles.heroDetailSection}>
          <div
            className={styles.heroDetail}
            dangerouslySetInnerHTML={createMarkup(
              props.originalResponse.hero.detail
            )}
          ></div>
        </section>

        <section className={styles.heroItems}>
          {props.originalResponse.hero_items
            .filter((itemA: ItemProps, i: number) => i % 4 == 0)
            .map((itemB: ItemProps, j: number) => (
              <FadeIn key={j} onMobile={false}>
                <Grid columns={4}>
                  {props.originalResponse.hero_items
                    .filter(
                      (itemC: ItemProps, k: number) =>
                        k >= j * 4 && k < j * 4 + 4
                    )
                    .map((item: ItemProps, index: number) => (
                      <Item
                        key={index}
                        {...item}
                        id={j * 4 + index + 1}
                        hasBorder={false}
                      />
                    ))}
                </Grid>
              </FadeIn>
            ))}
        </section>

        <section className={styles.aboutSection}>
          <h2 className={styles.aboutSectionTitle}>
            {props.originalResponse.aboutsection.title}
          </h2>
          <div
            className={styles.aboutSectionDetail}
            dangerouslySetInnerHTML={createMarkup(
              props.originalResponse.aboutsection.detail
            )}
          ></div>
          <div className={styles.aboutSectionImage}>
            <CustomImage
              src={props.originalResponse.aboutsection}
              layout="responsive"
            />
          </div>
          <div className={styles.aboutSectionContent}>
            <div
              className={styles.aboutSectionContentDetail}
              dangerouslySetInnerHTML={createMarkup(
                props.originalResponse.aboutsection.main_text
              )}
            />
            <div>
              {/* <div className={styles.aboutSectionGoalValue}>
                {props.originalResponse.aboutsection.goal_value}
              </div>
              <div
                className={styles.aboutSectionGoalDetail}
                dangerouslySetInnerHTML={createMarkup(
                  props.originalResponse.aboutsection.goal_detail
                )}
              ></div> */}
            </div>
          </div>
        </section>

        {/* <section className={styles.aboutSectionItems}>
          <div className={styles.aboutSectionItemsContent}>
            {props.originalResponse.about_section_items.map(
              (item: any, index: number) => (
                <div className={styles.aboutSectionItem} key={index}>
                  <h4
                    className={styles.aboutSectionItemTitle}
                    dangerouslySetInnerHTML={createMarkup(item.title)}
                  ></h4>
                  <div
                    className={styles.aboutSectionItemDetail}
                    dangerouslySetInnerHTML={createMarkup(item.detail)}
                  ></div>
                </div>
              )
            )}
          </div>
          <div
            className={styles.disclaimer}
            dangerouslySetInnerHTML={createMarkup(
              props.originalResponse.aboutsection.disclaimer
            )}
          ></div>
        </section> */}

        {/* <section id="map" className={styles.mapSection}>
          <h2 className={styles.mapSectionTitle}>
            {props.originalResponse.mapsection.title}
          </h2>
          <div
            className={styles.mapSectionDetail}
            dangerouslySetInnerHTML={createMarkup(
              props.originalResponse.mapsection.detail
            )}
          ></div>

          <div className={styles.mapSectionImageContainer}>
            <div className={styles.mapSectionImage}>
              <MapSection
                mapsection={props.originalResponse.mapsection}
              ></MapSection>
            </div>
          </div>

          <div className={styles.hotspotKeys}>
            <div>
              <span className={styles.hotspotKey}>P</span>
              <span className={styles.hotspotKeyTitle}>Proposed</span>
            </div>

            <div>
              <span className={styles.hotspotKey}>ID</span>
              <span className={styles.hotspotKeyTitle}>In Development</span>
            </div>

            <div>
              <span className={styles.hotspotKey}>EP</span>
              <span className={styles.hotspotKeyTitle}>Energy Producing</span>
            </div>
          </div>
        </section> */}

        <section className={styles.processStationsSection}>
          {isMobile && (
            <div className={styles.processStationsSectionTitleContainer}>
              <h2 className={styles.processStationsSectionTitle}>
                {props.originalResponse.processstationssection.title}
              </h2>
            </div>
          )}
          <div className={styles.processStationsInner}>
            <div className={styles.processStationsImage}>
              {props.originalResponse.process_stations_blocks.map(
                (block: any, index: number) => (
                  <div
                    key={index}
                    className={`${
                      index == processImageActive
                        ? styles.processStationsImageBlockActive
                        : styles.processStationsImageBlock
                    }`}
                    style={{
                      opacity: index == processImageActive ? 1 : imageOpacity,
                    }}
                  >
                    <CustomImage
                      src={block}
                      layout="responsive"
                      objectFit="contain"
                      priority
                    />
                  </div>
                )
              )}
            </div>

            <div className={styles.processStationsContentContainer}>
              <div className={styles.processStationsContent}>
                {!isMobile && (
                  <h2 className={styles.processStationsSectionTitle}>
                    {props.originalResponse.processstationssection.title}
                  </h2>
                )}
                <div className={styles.processStationBlocksContainer}>
                  {props.originalResponse.process_stations_blocks.map(
                    (block: any, index: number) => (
                      <ProcessSectionBlock
                        key={index}
                        index={index}
                        block={block}
                        handleActive={() => setProcessImageActive(index)}
                        processImageActive={processImageActive}
                      />
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className={styles.detailSection}>
          <div
            className={styles.detailSectionDetail}
            dangerouslySetInnerHTML={createMarkup(
              props.originalResponse.detailsection.detail
            )}
          ></div>
          <Grid columns={2}>
            <div
              className={styles.detailSectionSecondaryDetail}
              dangerouslySetInnerHTML={createMarkup(
                props.originalResponse.detailsection.secondary_detail
              )}
            ></div>
            <CustomImage
              src={props.originalResponse.detailsection}
              alt={props.originalResponse.detailsection.alt_text}
            />
          </Grid>
        </section>

        <section className={styles.imageQuoteSection}>
          <ImageQuote
            quote={props.originalResponse.imagequotesection.quote}
            author={props.originalResponse.imagequotesection.author}
            image={props.originalResponse.imagequotesection}
          />
        </section>
        {/* <section className={styles.beyondBatteriesSection}>
          <h2 className={styles.beyondBatteriesTitle}>
            {props.originalResponse.beyondbatteriessection.title}
          </h2>
          <div
            className={styles.beyondBatteriesDetail}
            dangerouslySetInnerHTML={createMarkup(
              props.originalResponse.beyondbatteriessection.detail
            )}
          ></div>
          {isMobile ? (
            <Swiper
              spaceBetween={parseInt(variables.phoneMargin)}
              loop={true}
              slidesPerView="auto"
              navigation={true}
              modules={[Navigation]}
            >
              {props.originalResponse.beyond_batteries_blocks.map(
                (block: any, index: number) => (
                  <SwiperSlide key={block.id}>
                    <div className={styles.beyondBatteriesBlock} key={index}>
                      <div className={styles.beyondBatteriesBlockImage}>
                        <CustomImage src={block} layout="responsive" />
                      </div>
                      <h5 className={styles.blockTitle}>{block.title}</h5>
                    </div>
                  </SwiperSlide>
                )
              )}
            </Swiper>
          ) : (
            <Grid columns={3}>
              {props.originalResponse.beyond_batteries_blocks.map(
                (block: any, index: number) => (
                  <div className={styles.beyondBatteriesBlock} key={index}>
                    <div className={styles.beyondBatteriesBlockImage}>
                      <CustomImage src={block} layout="responsive" />
                    </div>
                    <h5 className={styles.blockTitle}>{block.title}</h5>
                  </div>
                )
              )}
            </Grid>
          )}
        </section> */}

        {props.originalResponse.careers_section && (
          <Careers careersSection={props.originalResponse.careers_section} />
        )}
      </div>
    </div>
  )
}

export async function getStaticProps() {
  const page: GenericPageLayoutProps = await parseGenericPage(
    `${getAPI()}/kavianpage/`
  )
  return {
    props: {
      originalResponse: page.originalResponse,
      title: page.title,
      globalData: page.globalData,
      meta_title: page.meta_title,
      meta_description: page.meta_description,
    },
    revalidate: NEXTJS_REVALIDATE_PAGE_IN_SECONDS,
  }
}

export const NEXTJS_REVALIDATE_PAGE_IN_SECONDS = 10
export default PlatformManufacturing
